import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const PICKER_CONFIG = [
  {
    selector: '[data-rps-stand-picker]',
    buttonSelector: '[data-rps-add-stand]',
    statusSelector: '[data-rps-stand-status]',
    radioPrefix: 'rps-stand-',
    skipTitle: 'NO STAND',
    skipText: 'Skip the stand add-on.',
    instruction: 'Choose a stand or No Stand. Your selection will be added with the instrument when you press Add to Cart below.',
  },
  {
    selector: '[data-rps-bench-picker]',
    buttonSelector: '[data-rps-add-bench]',
    statusSelector: '[data-rps-bench-status]',
    radioPrefix: 'rps-bench-',
    skipTitle: 'NO BENCH',
    skipText: 'Skip the bench add-on.',
    instruction: 'Choose a bench or No Bench. Your selection will be added with the instrument when you press Add to Cart below.',
  },
  {
    selector: '[data-rps-pedal-picker]',
    buttonSelector: '[data-rps-add-pedal]',
    statusSelector: '[data-rps-pedal-status]',
    radioPrefix: 'rps-pedal-',
    skipTitle: 'NO PEDAL ADD-ON',
    skipText: 'Use the included pedal when applicable, or skip an optional pedal.',
    instruction: 'Choose a pedal option or No Pedal Add-On. Any selected pedal will be added with the instrument when you press Add to Cart below.',
  },
];

const getSection = (element) => element.closest('.shopify-section') || document;

function createSkipChoice(choices, firstRadio, config) {
  if (choices.querySelector('[data-rps-skip-option]')) return;

  const label = document.createElement('label');
  label.className = 'rps-stand-choice';
  label.dataset.rpsSkipOption = 'true';

  const radio = document.createElement('input');
  radio.type = 'radio';
  radio.name = firstRadio.name;
  radio.value = '';
  radio.checked = !choices.querySelector('input[type="radio"]:checked');

  const imageWrap = document.createElement('span');
  imageWrap.className = 'rps-stand-choice__image-wrap';
  const placeholder = document.createElement('span');
  placeholder.className = 'rps-bench-placeholder';
  placeholder.textContent = 'SKIP';
  imageWrap.appendChild(placeholder);

  const copy = document.createElement('span');
  copy.className = 'rps-stand-choice__copy';
  const strong = document.createElement('strong');
  strong.textContent = config.skipTitle;
  const fit = document.createElement('span');
  fit.className = 'rps-stand-choice__fit';
  fit.textContent = config.skipText;
  copy.append(strong, fit);

  label.append(radio, imageWrap, copy);
  choices.prepend(label);
}

function addBundleInstruction(root, config) {
  if (root.querySelector('[data-rps-bundle-instruction]')) return;

  const note = document.createElement('p');
  note.className = 'rps-stand-picker__disclaimer';
  note.dataset.rpsBundleInstruction = 'true';
  note.innerHTML = `<strong>${config.instruction}</strong>`;

  const existingDisclaimer = root.querySelector('.rps-stand-picker__disclaimer');
  if (existingDisclaimer) {
    root.insertBefore(note, existingDisclaimer);
  } else {
    root.appendChild(note);
  }
}

function enhancePicker(root, config) {
  if (!(root instanceof HTMLElement)) return;

  root.querySelector(config.buttonSelector)?.remove();
  root.querySelector(config.statusSelector)?.remove();

  const choices = root.querySelector('.rps-stand-choices');
  const firstRadio = choices?.querySelector(`input[type="radio"][name^="${config.radioPrefix}"]`);
  if (choices instanceof HTMLElement && firstRadio instanceof HTMLInputElement) {
    createSkipChoice(choices, firstRadio, config);
  }

  if (config.radioPrefix === 'rps-stand-') {
    const helper = root.querySelector('.rps-stand-picker__small');
    if (helper) helper.textContent = 'Optional. Select a stand below, or choose No Stand.';
  }

  addBundleInstruction(root, config);
  root.dataset.rpsBundleReady = 'true';
}

function arrangeBuilder(productForm) {
  if (!(productForm instanceof HTMLElement)) return;

  const productId = productForm.dataset.productId;
  if (!productId) return;

  const section = getSection(productForm);
  const buyButtonsBlock = productForm.closest('.buy-buttons-block');
  if (!(buyButtonsBlock instanceof HTMLElement)) return;

  const roots = [];
  for (const config of PICKER_CONFIG) {
    const root = section.querySelector(`${config.selector}[data-product-id="${productId}"]`);
    if (root instanceof HTMLElement) {
      enhancePicker(root, config);
      roots.push(root);
    }
  }

  if (!roots.length) return;

  for (const root of roots) {
    buyButtonsBlock.before(root);
  }

  buyButtonsBlock.dataset.rpsBundleCart = 'true';
  bindAccessoryCheckoutState(productForm);
}

function initBundleBuilders() {
  document.querySelectorAll('product-form-component[data-product-id]').forEach(arrangeBuilder);
}

function updateAcceleratedCheckoutState(productForm) {
  if (!(productForm instanceof HTMLElement)) return;

  const form = productForm.querySelector('form[data-type="add-to-cart-form"]');
  if (!(form instanceof HTMLFormElement)) return;

  form.querySelectorAll('[data-rps-additional-item]').forEach((input) => input.remove());

  const accessoryItems = getSelectedAccessoryItems(productForm);
  accessoryItems.forEach((item, index) => {
    const itemIndex = index + 1;

    const idInput = document.createElement('input');
    idInput.type = 'hidden';
    idInput.name = `shopify-additional-items[${itemIndex}][id]`;
    idInput.value = item.variantId;
    idInput.dataset.rpsAdditionalItem = 'true';

    const quantityInput = document.createElement('input');
    quantityInput.type = 'hidden';
    quantityInput.name = `shopify-additional-items[${itemIndex}][quantity]`;
    quantityInput.value = String(item.quantity || 1);
    quantityInput.dataset.rpsAdditionalItem = 'true';

    form.append(idInput, quantityInput);
  });

  const accelerated = productForm.querySelector('.accelerated-checkout-block');
  if (accelerated instanceof HTMLElement) accelerated.hidden = false;

  const buyButtons = productForm.closest('.buy-buttons-block');
  const oldNote = buyButtons?.querySelector('[data-rps-express-bundle-note]');
  if (oldNote instanceof HTMLElement) oldNote.remove();
}

function bindAccessoryCheckoutState(productForm) {
  if (!(productForm instanceof HTMLElement)) return;
  if (productForm.dataset.rpsExpressStateReady === 'true') return;
  productForm.dataset.rpsExpressStateReady = 'true';

  const productId = productForm.dataset.productId;
  if (!productId) return;

  const section = getSection(productForm);
  const selectors = PICKER_CONFIG.map((config) => `${config.selector}[data-product-id="${productId}"] input[type="radio"]`).join(',');

  section.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches(selectors)) return;
    updateAcceleratedCheckoutState(productForm);
  });

  updateAcceleratedCheckoutState(productForm);
}

function getSelectedAccessoryItems(productForm) {
  const productId = productForm.dataset.productId;
  if (!productId) return [];

  const section = getSection(productForm);
  const variantIds = new Set();

  for (const config of PICKER_CONFIG) {
    const root = section.querySelector(`${config.selector}[data-product-id="${productId}"]`);
    if (!(root instanceof HTMLElement)) continue;

    const selected = root.querySelector(`input[type="radio"][name^="${config.radioPrefix}"]:checked`);
    if (selected instanceof HTMLInputElement && selected.value) {
      variantIds.add(selected.value);
    }
  }

  return Array.from(variantIds).map((variantId) => ({ variantId, quantity: 1 }));
}

async function fetchCurrentCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to refresh cart: ${response.status}`);
  return response.json();
}

async function addBundle(productForm, items, submitter) {
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionIds = [];
  for (const item of cartItemsComponents) {
    if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
  }

  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  productForm.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: item.variantId, quantity: item.quantity })),
      promise: deferredEventPromise.promise,
    })
  );

  const payload = {
    items: items.map((item) => ({ id: Number(item.variantId), quantity: item.quantity })),
    sections: sectionIds.join(','),
  };

  if (submitter instanceof HTMLButtonElement) submitter.disabled = true;

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      const message = data.description || data.message || 'Unable to add the selected setup to the cart.';
      productForm.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'INVALID',
          detail: { description: data.description, errors: data.errors },
        })
      );

      const cart = await fetchCurrentCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: true,
          items: cart.items,
          source: 'rps-bundle-cart',
          sourceId: productForm.id || '',
          itemCount: totalQuantity,
          productId: productForm.dataset.productId,
        },
      });

      const liveRegion = productForm.querySelector('[ref="liveRegion"]');
      if (liveRegion) liveRegion.textContent = message;
      return;
    }

    const cart = await fetchCurrentCart();
    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'rps-bundle-cart',
        sourceId: productForm.id || '',
        itemCount: totalQuantity,
        productId: productForm.dataset.productId,
        sections: data.sections || {},
        didError: false,
      },
    });

    const liveRegion = productForm.querySelector('[ref="liveRegion"]');
    if (liveRegion) {
      liveRegion.textContent = items.length === 2 ? 'Instrument and selected add-on added to cart.' : `Instrument and ${items.length - 1} selected add-ons added to cart.`;
      window.setTimeout(() => {
        liveRegion.textContent = '';
      }, 5000);
    }
  } catch (error) {
    deferredEventPromise.reject(error);
    productForm.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Network error while adding the selected setup.',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
  } finally {
    if (submitter instanceof HTMLButtonElement) submitter.disabled = false;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBundleBuilders, { once: true });
} else {
  initBundleBuilders();
}

document.addEventListener('shopify:section:load', initBundleBuilders);
